import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, TrendingDown, AlertCircle } from "lucide-react";

const STAGE_ORDER = ["INVITED", "IN_PROGRESS", "SUBMITTED", "EVALUATED", "OFFERED", "HIRED"];
const STAGE_LABEL = { INVITED: "Invited", IN_PROGRESS: "In progress", SUBMITTED: "Submitted", EVALUATED: "Evaluated", OFFERED: "Offered", HIRED: "Hired" };

function daysColor(days) {
  if (days == null) return "bg-neutral-100 text-neutral-500";
  if (days <= 2) return "bg-teal/10 text-teal";
  if (days <= 5) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

export default function TimeToHireCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/time-to-hire").then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return null;
  const stages = STAGE_ORDER.map(s => ({ s, ...data.stage_stats[s] })).filter(x => x.count > 0 || x.avg_days_in_stage != null);
  const maxDays = Math.max(1, ...stages.map(x => x.avg_days_in_stage || 0));

  return (
    <Card className="border-0 shadow-sm" data-testid="time-to-hire-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Time to hire</h3>
          </div>
          <div className="text-xs text-neutral-500">
            {data.open_jobs} open roles · {data.open_candidates} active candidates
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl border p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Avg time-to-hire</div>
            <div className="font-mono text-2xl font-bold text-neutral-900 mt-0.5" data-testid="tth-avg">
              {data.avg_time_to_hire_days != null ? `${data.avg_time_to_hire_days}d` : "—"}
            </div>
          </div>
          <div className="rounded-xl border p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">p90 time-to-hire</div>
            <div className="font-mono text-2xl font-bold text-neutral-900 mt-0.5" data-testid="tth-p90">
              {data.p90_time_to_hire_days != null ? `${data.p90_time_to_hire_days}d` : "—"}
            </div>
          </div>
        </div>

        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Avg days per stage</div>
        <div className="space-y-2">
          {stages.map(x => (
            <div key={x.s} className="flex items-center gap-3 text-xs" data-testid={`tth-stage-${x.s}`}>
              <div className="w-24 shrink-0 text-neutral-700">{STAGE_LABEL[x.s]}</div>
              <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-pink transition-all" style={{ width: `${Math.min(100, ((x.avg_days_in_stage || 0) / maxDays) * 100)}%` }} />
              </div>
              <div className={`font-mono text-xs px-2 py-0.5 rounded ${daysColor(x.avg_days_in_stage)}`}>
                {x.avg_days_in_stage != null ? `${x.avg_days_in_stage}d` : "—"}
              </div>
              <div className="w-10 text-right text-neutral-500">{x.count}</div>
            </div>
          ))}
        </div>

        {data.top_bottlenecks?.length > 0 && (
          <div className="mt-5 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-3.5 h-3.5 text-red-500" />
              <div className="text-xs uppercase tracking-widest text-neutral-500">Top bottlenecks</div>
            </div>
            <div className="space-y-1.5">
              {data.top_bottlenecks.slice(0, 5).map(r => (
                <div key={r.job_id} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b last:border-b-0" data-testid={`tth-role-${r.job_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.title}</div>
                    <div className="text-[10px] text-neutral-500">{r.department} · {r.candidates} candidates</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.stuck_offers > 0 && (
                      <Badge className="text-[9px] bg-red-500 text-white border-0 gap-1"><AlertCircle className="w-2.5 h-2.5" /> {r.stuck_offers} offer{r.stuck_offers > 1 ? "s" : ""} stuck &gt;5d</Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {STAGE_LABEL[r.bottleneck_stage] || r.bottleneck_stage}: {r.bottleneck_days}d
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
